import { useRef, type ClipboardEvent, type KeyboardEvent } from 'react'

import FormError from '@/components/FormError'
import { cn } from '@/lib/misc/Utils'
import { GenericObject } from '@/lib/definitions/generic.types'
import useParseFormErrors from '@/lib/hooks/useParseFormErrors'

export const TOTP_CODE_LENGTH = 6

interface TotpCodeInputProps {
  value: string
  onChange: (digits: string) => void
  disabled?: boolean
  errors?: (string | undefined | GenericObject)[]
  label?: string
  autoFocus?: boolean
  inputDataCyPrefix?: string
  formErrorsDataCy?: string
}

const slotsFromCode = (code: string, length: number) =>
  Array.from({ length }, (_, i) => code[i] ?? '')

const TotpCodeInput = ({
  value,
  onChange,
  disabled = false,
  errors,
  label = 'Authentication code',
  autoFocus = false,
  inputDataCyPrefix = 'input-otp-code',
  formErrorsDataCy = 'form-errors-input-otp-code',
}: TotpCodeInputProps) => {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])
  const { parsedErrors } = useParseFormErrors(errors, undefined)

  const normalized = (value || '').replace(/\D/g, '').slice(0, TOTP_CODE_LENGTH)

  const commit = (digits: string) => {
    onChange(digits.replace(/\D/g, '').slice(0, TOTP_CODE_LENGTH))
  }

  const focusInput = (index: number) => {
    queueMicrotask(() => {
      const el = inputRefs.current[index]
      el?.focus()
      el?.select()
    })
  }

  const handleChange = (index: number, raw: string) => {
    const digitsOnly = raw.replace(/\D/g, '')
    const slots = slotsFromCode(normalized, TOTP_CODE_LENGTH)

    if (digitsOnly.length === 0) {
      slots[index] = ''
      commit(slots.join(''))
      return
    }

    if (digitsOnly.length === 1) {
      slots[index] = digitsOnly
      commit(slots.join(''))
      focusInput(index < TOTP_CODE_LENGTH - 1 ? index + 1 : index)
      return
    }

    let writeIndex = index
    for (const ch of digitsOnly) {
      if (writeIndex < TOTP_CODE_LENGTH) {
        slots[writeIndex] = ch
        writeIndex += 1
      }
    }
    commit(slots.join(''))
    focusInput(Math.min(writeIndex, TOTP_CODE_LENGTH - 1))
  }

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Backspace') {
      return
    }
    if (normalized[index]) {
      return
    }
    if (index === 0) {
      return
    }
    e.preventDefault()
    const slots = slotsFromCode(normalized, TOTP_CODE_LENGTH)
    slots[index - 1] = ''
    commit(slots.join(''))
    focusInput(index - 1)
  }

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const clipboardData = e.clipboardData
    if (!clipboardData) {
      return
    }
    const pasted = clipboardData
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, TOTP_CODE_LENGTH)
    commit(pasted)
    const nextIndex =
      pasted.length === 0 ? 0 : Math.min(pasted.length, TOTP_CODE_LENGTH - 1)
    focusInput(nextIndex)
  }

  const hasError = parsedErrors.length > 0

  return (
    <div className="relative">
      <div className="text-neutral-40 mb-2 text-sm">{label}</div>
      <div className="flex flex-wrap justify-center gap-2 sm:justify-start">
        {Array.from({ length: TOTP_CODE_LENGTH }, (_, index) => (
          <input
            key={index}
            ref={(el) => {
              inputRefs.current[index] = el
            }}
            data-cy={`${inputDataCyPrefix}-${index}`}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={1}
            autoFocus={autoFocus && index === 0}
            autoComplete={index === 0 ? 'one-time-code' : 'off'}
            disabled={disabled}
            aria-label={`${label} digit ${index + 1} of ${TOTP_CODE_LENGTH}`}
            className={cn(
              'inter-input-base text-center text-lg tabular-nums rounded-lg border border-solid bg-white border-neutral-70 size-12 focus:border-2 focus:border-brand-5 focus:outline-hidden hover:border-neutral-40 disabled:bg-neutral-10 disabled:border-neutral-70 disabled:pointer-events-none',
              hasError && 'border-brand-6! focus:border-brand-6'
            )}
            value={normalized[index] ?? ''}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
          />
        ))}
      </div>
      {parsedErrors.map(
        (error, errIndex) =>
          !!error && (
            <FormError cy={formErrorsDataCy} key={errIndex}>
              {error}
            </FormError>
          )
      )}
    </div>
  )
}

export default TotpCodeInput
